import "./style.scss";
import { FC, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import LogoSvg from "@/src/svg/default/LogoSvg";
import BtnDefault from "../../../../../BtnDefault/BtnDefault";
import { AOPPolandAdmissionData } from "@/src/types/admissionOffice";
import { Editor } from "@tinymce/tinymce-react";
import { splitHtmlByBlocks, splitHtmlByBlocksWithFontConvert } from "../../../../converterPtToRem";
import AutocompleteInput from "@/src/components/AutocompleteInput/AutocompleteInput";
import { useAutocomplete } from "@/src/hooks/useAutocomplete";
import { admissionOfficeRequests } from "@/src/api/admissionOfficeDocCreateRequests";
import {
  listPolandEnAdmissionSignatures,
  templatesPolandEnAdmissionCoursesSettings,
  templatesPolandEnInvoiceCoursesSettings,
} from "@/src/constants/AdmissionOffice/Poland/AOPolandEnConsts";
import { Nationalities } from "@/src/constants/globalConsts";
import { admissionOfficeWhoseDoc } from "@/src/constants/AdmissionOffice/admissionsOfficeConsts";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import { useDispatch } from "react-redux";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";

interface IAOPFormValues {
  whose_doc: string;
  currentDate: string;
  fullName: string;
  dateOfBirth: string;
  nationality: string;
  course: string;
  selectedCourse: string;
  mediumOfInstruction: string;
  duration: string;
  invoiceBody: string;
  signature: string;
  coursePrefix: string;
}

interface AOPConfigProps {
  dataForGeneration: AOPPolandAdmissionData;
  setDataForGeneration: React.Dispatch<React.SetStateAction<AOPPolandAdmissionData>>;
  setBodyForPrev: React.Dispatch<React.SetStateAction<AOPPolandAdmissionData>>;
}

const today = new Date();
const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AOPConfig: FC<AOPConfigProps> = ({ dataForGeneration, setDataForGeneration, setBodyForPrev }) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<IAOPFormValues>({
    defaultValues: {
      invoiceBody: "",
      currentDate: formatDateForInput(today),
    },
  });
  const dispatch = useDispatch();
  const [content, setContent] = useState<string>("");
  const [invoiceContent, setInvoiceContent] = useState<string>("");
  const [title, setTitle] = useState<string>("");

  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);

  const course = watch("course");
  const invoiceBody = watch("invoiceBody");

  // AUTOCOMPLETE ↓
  const signatureAuto = useAutocomplete(Object.keys(listPolandEnAdmissionSignatures));
  const offeredCourseAuto = useAutocomplete(Object.keys(templatesPolandEnAdmissionCoursesSettings));
  const invoiceBodyAuto = useAutocomplete(Object.keys(templatesPolandEnInvoiceCoursesSettings));
  const nationalitiesAuto = useAutocomplete(Nationalities);
  const whoseDocAuto = useAutocomplete(admissionOfficeWhoseDoc);

  // EDITORS
  useEffect(() => {
    if (course) {
      const key = course as keyof typeof templatesPolandEnAdmissionCoursesSettings;
      const courseSettings = templatesPolandEnAdmissionCoursesSettings[key];
      if (courseSettings) {
        setContent(courseSettings.tinyMCEContent);
        setValue("selectedCourse", courseSettings.selected_course);
        setValue("mediumOfInstruction", courseSettings.medium_of_instruction);
        setValue("duration", courseSettings.duration);
        setTitle(courseSettings.title);
      }
    }
  }, [course, setValue]);

  useEffect(() => {
    if (invoiceBody) {
      const key = invoiceBody as keyof typeof templatesPolandEnInvoiceCoursesSettings;
      const invoiceSettings = templatesPolandEnInvoiceCoursesSettings[key];
      setInvoiceContent(invoiceSettings ? invoiceSettings.tinyMCEContent : "");
    }
  }, [invoiceBody]);

  const generateNewDocument = async (values: IAOPFormValues) => {
    dispatch(setIsPreloyder(true));
    try {
      let response = await admissionOfficeRequests.createPolandInvoiceDoc(dataForGeneration);
      if (response) {
        setIsGenerated(true);
      }
    } catch (error) {
      alert("An unexpected error occurred - write to the developers");
    } finally {
      dispatch(setIsPreloyder(false));
    }
    setIsPreviewReady(false);
  };

  const handlePreview = () => {
    const watchedFields = {
      coursePrefix: watch("coursePrefix"),
      selectedCourse: watch("selectedCourse"),
      duration: watch("duration"),
      fullName: watch("fullName"),
      mediumOfInstruction: watch("mediumOfInstruction"),
      nationality: watch("nationality"),
      title: title,
      whose_doc: watch("whose_doc"),
      currentDate: watch("currentDate"),
      dateOfBirth: watch("dateOfBirth"),
    };
    setDataForGeneration((prev) => ({
      ...prev,
      course: watchedFields.selectedCourse || "",
      coursePrefix: watchedFields.coursePrefix || "",
      duration: watchedFields.duration || "",
      fullName: watchedFields.fullName || "",
      mediumOfInstruction: watchedFields.mediumOfInstruction || "",
      nationality: watchedFields.nationality || "",
      title: watchedFields.title || "",
      whose_doc: watchedFields.whose_doc || "",
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      invoiceBody: splitHtmlByBlocks(invoiceContent),
    }));
    setBodyForPrev((prev) => ({
      ...prev,
      course: watchedFields.selectedCourse || "",
      coursePrefix: watchedFields.coursePrefix || "",
      duration: watchedFields.duration || "",
      fullName: watchedFields.fullName || "",
      mediumOfInstruction: watchedFields.mediumOfInstruction || "",
      nationality: watchedFields.nationality || "",
      title: watchedFields.title || "",
      whose_doc: watchedFields.whose_doc || "",
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      invoiceBody: splitHtmlByBlocksWithFontConvert(invoiceContent),
    }));
    const hasContent = content.trim().length > 0 || invoiceContent.trim().length > 0;
    if (hasContent) {
      setIsPreviewReady(true);
      setIsGenerated(false);
    }
  };

  const handleInvoiceEditorChange = (newInvoiceContent: string) => setInvoiceContent(newInvoiceContent);

  function parseDate(value: string) {
    const [year, month, day] = value.split("-");
    return `${day}.${month}.${year}`;
  }

  const handleCopy = async (fieldName: string) => {
    const value = getValues(fieldName as any);
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch (err) {
      console.error("Ошибка копирования", err);
    }
  };

  return (
    <>
      <div className="admission-office-page-config">
        <div className="admission-office-page-config-logo">
          <div className="logo-svg">
            <LogoSvg />
          </div>
          <div className="logo-line"></div>
          <p>Platforma</p>
        </div>
        <p className="admission-office-page-config-title">ADMISSION/CONFIRMATION LETTER</p>
        <form className="admission-office-page-config-form">
          <div className="form-group-fields">
            <div className="form-group-fields-container">
              <div className="form-group-fields-input whose_doc">
                <AutocompleteInput
                  placeholder="Whose"
                  value={whoseDocAuto.query}
                  onChange={whoseDocAuto.onChange}
                  onSelect={(v) => {
                    whoseDocAuto.onSelect(v);
                    setValue("whose_doc", v);
                  }}
                  isOpen={whoseDocAuto.isOpen}
                  options={whoseDocAuto.filtered}
                  error={!!errors.invoiceBody}
                  required
                  inputProps={{
                    register: register("whose_doc", { required: true }),
                    close: whoseDocAuto.close,
                    setOpen: whoseDocAuto.onClick,
                  }}
                  identifier={"whose"}
                />
              </div>
              <div className="form-group-fields-input coursePrefix">
                <input className="coursePrefix" id="coursePrefix" type="text" {...register("coursePrefix")} />
              </div>
            </div>
            <div className="form-group-fields-input currentDate">
              <div className="form-group-fields-input-label">
                <label htmlFor="currentDate">
                  Date of Issue<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="currentDate" id="currentDate" type="date" {...register("currentDate", { required: true })} />
              {errors.currentDate && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="full-name">
                  Full Name<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="full-mame" id="full-mame" type="text" {...register("fullName", { required: true })} />
              {errors.fullName && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullName")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="dateOfBirth">
                  Data of Birth<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="dateOfBirth" id="dateOfBirth" type="date" {...register("dateOfBirth", { required: true })} />
              {errors.dateOfBirth && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
            <AutocompleteInput
              label="Nationality"
              value={nationalitiesAuto.query}
              onChange={nationalitiesAuto.onChange}
              onSelect={(v) => {
                nationalitiesAuto.onSelect(v);
                setValue("nationality", v);
              }}
              isOpen={nationalitiesAuto.isOpen}
              options={nationalitiesAuto.filtered}
              error={!!errors.nationality}
              required
              inputProps={{
                register: register("nationality", { required: true }),
                close: nationalitiesAuto.close,
                setOpen: nationalitiesAuto.onClick,
              }}
              identifier={"nationality"}
            />
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input course">
              <AutocompleteInput
                label="Select the offered course"
                value={offeredCourseAuto.query}
                onChange={offeredCourseAuto.onChange}
                onSelect={(v) => {
                  offeredCourseAuto.onSelect(v);
                  setValue("course", v);
                }}
                isOpen={offeredCourseAuto.isOpen}
                options={offeredCourseAuto.filtered}
                error={!!errors.course}
                required
                inputProps={{
                  register: register("course", { required: true }),
                  close: offeredCourseAuto.close,
                  setOpen: offeredCourseAuto.onClick,
                  setValue,
                  clearErrors,
                  name: "course",
                }}
                identifier={"offered-course-auto"}
              />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="selected-сourse">
                  Selected course<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="selected-сourse" id="selected-сourse" type="text" {...register("selectedCourse", { required: true })} />
              {errors.selectedCourse && <p>This field is required</p>}
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="medium-of-instruction">
                  Medium of Instruction<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input
                className="medium-of-instruction"
                id="medium-of-instruction"
                type="text"
                {...register("mediumOfInstruction", { required: true })}
              />
              {errors.mediumOfInstruction && <p>This field is required</p>}
            </div>
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="duration">
                  Duration<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="duration" id="duration" type="text" {...register("duration", { required: true })} />
              {errors.duration && <p>This field is required</p>}
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input invoiceBody">
              <AutocompleteInput
                label="Invoice details"
                value={invoiceBodyAuto.query}
                onChange={invoiceBodyAuto.onChange}
                onSelect={(v) => {
                  invoiceBodyAuto.onSelect(v);
                  setValue("invoiceBody", v);
                }}
                isOpen={invoiceBodyAuto.isOpen}
                options={invoiceBodyAuto.filtered}
                error={!!errors.invoiceBody}
                required
                inputProps={{
                  register: register("invoiceBody", { required: true }),
                  close: invoiceBodyAuto.close,
                  setOpen: invoiceBodyAuto.onClick,
                }}
                identifier={"invoice"}
              />
            </div>
          </div>
          <Editor
            value={invoiceContent}
            init={{
              height: 300,
              menubar: false,
              plugins: ["lists", "link", "code", "table"],
              toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
              branding: false,
              toolbar_mode: "wrap",
              licenseKey: "gpl",
              license_key: "gpl" as any,
              content_style: `
                body { font-family: Arial, sans-serif; padding: 1rem; margin: 0; }
                p { margin: 0; }
              `,
            }}
            onEditorChange={handleInvoiceEditorChange}
            tinymceScriptSrc="/tinymce/tinymce.min.js"
          />
          <div className="form-group-buttons">
            <div className="form-group-buttons-pack">
              <BtnDefault onClick={handlePreview} reverse={true}>
                <p>Preview doc</p>
                <AODSvgExport.PreviewSvg />
              </BtnDefault>
              <BtnDefault onClick={handleSubmit(generateNewDocument)} disabled={!isPreviewReady}>
                <p>Generate doc</p>
                <AODSvgExport.GenerateSvg />
              </BtnDefault>
              <BtnDefault disabled={!isGenerated}>
                <p>Download PDF</p>
                <AODSvgExport.DownloadSvg />
              </BtnDefault>
            </div>
            <div className="form-group-buttons-refresh" onClick={() => window.location.reload()}>
              <BtnDefault onlyIcon={true}>
                <AODSvgExport.RefreshSvg />
              </BtnDefault>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input signature">
              <AutocompleteInput
                label="Signature"
                value={signatureAuto.query}
                defaultValue="Fedorova Anzhelika"
                onChange={signatureAuto.onChange}
                onSelect={(v) => {
                  signatureAuto.onSelect(v);
                  setValue("signature", v);
                }}
                isOpen={signatureAuto.isOpen}
                options={signatureAuto.filtered}
                error={!!errors.signature}
                required
                inputProps={{
                  register: register("signature", { required: true }),
                  close: signatureAuto.close,
                  setOpen: signatureAuto.onClick,
                }}
                identifier={"signature"}
              />
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default AOPConfig;
